import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react'
import { AppForms, Chat, theme } from '@/config'
import {
  View,
  Text,
  PoweredBy,
  Button,
  UserList,
  ModalPage,
  ModalPageProps,
  ProfileBanner,
  GroupDetailsModalProps,
  MemberOptionsModal,
  ModalPageHeader,
  Icon,
} from '@/components'
import { APIClient, Getstream } from '@/services'
import { LayoutPanel, useChatPreviewAvatar, useChatProps } from '@/hooks'
import { createStyles } from '@codeleap/styles'
import { appStatusStore } from '@/stores'
import { alert } from '@codeleap/modals'
import { membersToUserList } from '@packages/chat'
import { getFlatlistProps } from '@/utils'
import type { AlertOptionWithDebug } from '@/components/modals/Alert'

export function GroupInfo(props: Partial<ModalPageProps> & GroupDetailsModalProps) {
  const { channel } = props
  Chat.Hooks.useSyncChannelMembers(channel)

  if (!channel) {
    return null
  }

  return <Content {...props} />
}

const Content = (props: Partial<ModalPageProps> & GroupDetailsModalProps) => {
  const { toggle, channel, navigateTo } = props
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 })
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const previewAvatar = useChatPreviewAvatar({ channel })
  const { displayTitle, membersCount, user } = useChatProps({ channel })
  const isRefreshingMembers = Chat.Store.useIsRefreshingMembers()
  const { isAdmin, isGroupOwner } = Getstream.Chat.useMyMembership(channel)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const toggleMenu = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }

    setMenuPosition({ top: 60, right: 20 })

    setIsMenuOpen(prev => !prev)
  }, [])

  const handleEdit = () => {
    AppForms.chatGroup.setValues({
      name: displayTitle,
      file: { preview: null },
    })
    navigateTo('edit_group')
    setIsMenuOpen(false)
  }

  const handleMentionNotifications = () => {
    navigateTo('mentions_notifications')
    setIsMenuOpen(false)
  }

  const channelId = useMemo(() => channel?.id, [channel])

  const members = Chat.Queries.members.useMemberList({ channelId })
  const flatList = getFlatlistProps(members)
  const query = members?.query
  const memberItems = members?.items || []

  const userActions = useMemo(() => {
    const actions = []

    if (isAdmin) {
      const addMemberItem = {
        debugName: 'GroupDetailsModal:addMember',
        icon: 'user-plus-01',
        onPress: () => {
          AppForms.chatGroup.resetValues()
          navigateTo('add_members')
        },
      }
      actions.push(addMemberItem)
    }

    return actions
  }, [isAdmin])

  async function handleExitGroup() {
    appStatusStore.set('loading')

    Chat.Services.leaveGroup({
      channelId: channel.id,
      member: user,
      onSuccess: async () => {
        props.toggle?.()
        Chat.Store.setChannel(null)
        await LayoutPanel.showLeftPanel()
      },
      onFinally: () => {
        appStatusStore.set('idle')
      },
    })
  }

  const menuItems = useMemo(() => {
    const items = [
      {
        label: 'Mentions notifications',
        icon: 'at-sign' as const,
        onPress: handleMentionNotifications,
      },
    ]

    if (isAdmin) {
      items.unshift({
        label: 'Edit',
        icon: 'edit-02' as any,
        onPress: handleEdit,
      })
    }

    return items
  }, [handleEdit, handleMentionNotifications, isAdmin])

  return (
    <ModalPage
      title='Information'
      canGoBack
      onPrevPage={toggle}
      toggle={toggle}
      actionProps={{
        icon: 'more-vertical',
        onPress: toggleMenu,
        debugName: 'GroupDetailsModal:menu',
      }}>
      <View style={['column', 'paddingVertical:2', 'pt:1']}>
        <UserList
          {...flatList}
          ListHeaderComponent={() => (
            <ListHeader
              displayImage={previewAvatar}
              displayTitle={displayTitle}
              membersCount={membersCount}
              isRefreshingMembers={isRefreshingMembers}
              userActions={userActions}
            />
          )}
          onPress={(u) => {
            const member = memberItems.find((m) => m.user_id === u.id)
            MemberOptionsModal.open({
              member,
              isAdmin,
              channel,
              onRemoveSuccess: query?.refetch,
            })
          }}
          roleShown
          noInteractableUsers={[user?.id]}
          data={membersToUserList(memberItems)}
          style={{
            wrapper: {
              maxHeight: 'min(70vh, 1000px)',
              overflow: 'auto',
              [theme.media.down('tabletSmall')]: {
                maxHeight: `calc(100vh - ${ModalPageHeader + 70}px )`,
              },
            },
          }}
        />

        <View style={['column', 'gap:2']}>
          {!isGroupOwner && (
            <Button
              debugName='GroupDetailsModal:exitGroup'
              text='Exit group'
              icon='corner-down-left'
              style={['row', 'row:negative', 'padding:2']}
              onPress={() => {
                alert.warn({
                  title: 'Exit Group',
                  body: 'Are you sure you want to exit this group?',
                  options: [
                    { text: 'Cancel', style: 'minimal', debugName: 'ExitGroupAlert:cancel' },
                    { text: 'Exit', style: 'destructive', onPress: handleExitGroup, debugName: 'ExitGroupAlert:confirm' },
                  ] as AlertOptionWithDebug[],
                })
              }}
            />)}

          <PoweredBy />
        </View>
      </View>

      {isMenuOpen && (
        <View
          ref={menuRef}
          style={{
            ...styles.menu,
            position: 'fixed',
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            zIndex: 99999,
          }}
        >
          {menuItems.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && (
                <View style={styles.separator} />
              )}
              <View
                debugName={`GroupDetailsModal:menu:${item.label}`}
                style={styles.menuItem}
                onClick={(e) => {
                  e.stopPropagation()
                  item.onPress()
                }}
              >
                <Text
                  text={item.label}
                  style={['p3', 'medium', { color: theme.colors.textHeadingRegular }]}
                />
                <Icon
                  name={item.icon}
                  style={{
                    fontSize: 24,
                    width: 24,
                    height: 24,
                    color: theme.colors.textHeadingRegular,
                    flexShrink: 0,
                  }}
                />
              </View>
            </React.Fragment>
          ))}
        </View>
      )}
    </ModalPage>
  )
}

function ListHeader({ displayImage, displayTitle, membersCount, isRefreshingMembers, userActions }) {
  const { companyName } = APIClient.Session.useCompanyBranding()

  return (
    <View style={['column', 'alignStart', 'justifyStart', 'gap:2']}>
      <ProfileBanner
        avatar={displayImage}
        name={displayTitle}
        logoShown={false}
        description={`Group - ${membersCount} members`}
        descriptionTestId='groupdetailsmodal:membercount'
        descriptionLoading={isRefreshingMembers}
        actions={userActions}
      />

      <Info title='Organisation' text={companyName} />

      <Text
        data-testid='groupdetailsmodal:membersheading'
        text='Members'
        style={['medium', 'p3', 'mb:1', 'ml:2', { color: theme.colors.textHeadingHighlighted }]}
      />
    </View>
  )
}

function Info({ title, text }) {
  return (
    <View style={styles.infoWrapper}>
      <Text text={title} style={['medium', 'c2', { color: theme.colors.textCaption }]} />
      <Text text={text} style={['p3', 'medium', { color: theme.colors.textHeadingHighlighted }]} />
    </View>
  )
}

const styles = createStyles(theme => ({
  menu: {
    backgroundColor: '#fff',
    borderRadius: theme.radius.regular,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    minWidth: 260,
    width: 260,
    overflow: 'hidden',
    border: `1px solid ${theme.baseColors.neutralBlackTransparent100}`,
    display: 'flex',
    flexDirection: 'column',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.value(1),
    padding: `${theme.spacing.value(1)}px ${theme.spacing.value(2)}px`,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',

    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.stroke,
    marginHorizontal: theme.spacing.value(1),
  },
  infoWrapper: {
    ...theme.presets.column,
    ...theme.presets.alignStart,
    ...theme.presets.justifyStart,
    ...theme.presets.fullWidth,
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.gap(0.2),
    ...theme.spacing.paddingBottom(1.5),
    ...theme.border({
      directions: ['bottom'],
      color: 'neutralSolid300',
    }),
  },
}))
